import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, FileStack, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';

function ArtifactSpecCard({ artifact }) {
  const isAnalogy = artifact.artifact_type === 'Real World Analogy';
  const visuals = artifact.expected_visual_elements || [];

  return (
    <div className="rounded-lg border border-sky-500/20 bg-sky-950/15 p-3 space-y-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-sky-200">Artifact: {artifact.artifact_type}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-600 text-slate-400">
          {artifact.complexity}
        </span>
        {artifact.pedagogical_role ? (
          <span className="text-[10px] text-slate-500">{artifact.pedagogical_role}</span>
        ) : null}
      </div>
      {artifact.purpose ? (
        <div>
          <p className="text-slate-500">Purpose</p>
          <p className="text-slate-200">{artifact.purpose}</p>
        </div>
      ) : null}
      {isAnalogy && artifact.analogy ? (
        <div>
          <p className="text-slate-500">Analogy</p>
          <p className="text-slate-200 italic">{artifact.analogy}</p>
        </div>
      ) : null}
      {visuals.length > 0 ? (
        <div>
          <p className="text-slate-500">Visual Elements</p>
          <ul className="list-disc pl-4 text-slate-300 space-y-0.5">
            {visuals.map((v) => (
              <li key={v}>{v}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {artifact.expected_teaching_value ? (
        <div>
          <p className="text-slate-500">Teaching Value</p>
          <p className="text-slate-300">{artifact.expected_teaching_value}</p>
        </div>
      ) : null}
      {artifact.generation_instructions ? (
        <div>
          <p className="text-slate-500">Generation Instructions</p>
          <p className="text-slate-400 leading-relaxed">{artifact.generation_instructions}</p>
        </div>
      ) : null}
    </div>
  );
}

function SlideSpecCard({ slide, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const artifacts = slide.artifacts || [];

  return (
    <div className="rounded-xl border border-slate-700/60 bg-slate-900/40 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-slate-800/40 transition-colors"
      >
        {open ? (
          <ChevronDown className="w-4 h-4 text-violet-400 shrink-0 mt-0.5" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-violet-100">
            {slide.slide_id}
            <span className="text-slate-500 font-normal"> — {slide.concept}</span>
          </p>
          <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{slide.learning_objective}</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <span className="text-[10px] text-slate-600">{slide.difficulty_level}</span>
            <span className="text-[10px] text-slate-600">·</span>
            <span className="text-[10px] text-slate-600">{artifacts.length} artifact{artifacts.length === 1 ? '' : 's'}</span>
          </div>
        </div>
      </button>
      {open ? (
        <div className="px-3 pb-3 space-y-3 border-t border-slate-800/80 pt-3">
          {slide.prerequisites?.length ? (
            <p className="text-[10px] text-slate-500">
              Prerequisites: {slide.prerequisites.join(', ')}
            </p>
          ) : null}
          {Object.keys(slide.artifact_selection_scores || {}).length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {Object.entries(slide.artifact_selection_scores).map(([name, score]) => (
                <span
                  key={name}
                  className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/25 text-amber-200/80"
                >
                  {name}: {score}
                </span>
              ))}
            </div>
          ) : null}
          {artifacts.map((art) => (
            <ArtifactSpecCard key={`${slide.slide_id}-${art.artifact_type}`} artifact={art} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ArtifactSpecificationV3Panel({ data }) {
  const slides = data?.slide_specifications || [];
  const [filter, setFilter] = useState('');

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return slides;
    return slides.filter(
      (s) => s.concept?.toLowerCase().includes(q)
        || s.slide_id?.toLowerCase().includes(q)
        || s.learning_objective?.toLowerCase().includes(q),
    );
  }, [slides, filter]);

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-violet-500/25 bg-violet-950/20 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-violet-200">
          <FileStack className="w-4 h-4" />
          {data.total_artifact_specs} artifact specs
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Layers className="w-4 h-4" />
          {data.total_slides} slides
        </div>
        {data.source ? (
          <span className="text-[10px] text-slate-500 ml-auto">Source: {data.source}</span>
        ) : null}
      </div>

      {slides.length > 6 ? (
        <input
          type="search"
          placeholder="Filter by concept or slide…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full text-sm rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-slate-200 placeholder:text-slate-600"
        />
      ) : null}

      <div className="space-y-2 max-h-[65vh] overflow-y-auto">
        {filtered.map((slide, i) => (
          <SlideSpecCard key={slide.slide_id} slide={slide} defaultOpen={i < 2} />
        ))}
      </div>
    </div>
  );
}
